import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Card, IconButton, InputAdornment, Stack, Tooltip } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useBlocker, useLocation } from 'react-router';
import { InferType } from 'yup';

import { WarningRounded } from '@mui/icons-material';
import {
  EmptyContent,
  enqueueSnackbar,
  FormProvider,
  FullPageSpinner,
  RHFAutocomplete,
  RHFDatePicker,
  RHFReactNumberFormat,
  RHFTextField,
  useBoolean,
  useDefaultEnumsStore,
  useDirtyFieldsStore,
  useFactFindConfigStore,
  useFactFindErrorStore,
  useFactFindProcessStore,
  useUserAccessStore,
  useUserDetailsStore,
} from '@zeta-core';
import { PlusIcon } from '@zeta-core/assets';
import { AIInteractionService, IFactFindProcessStatus, InteractionSubSectionType, InvestorService, IPatchAIInteractionSectionRequest } from '@zeta-services';
import {
  FactFindType,
  ICaptureFactFindBeneficiaryRequest,
  IClientCardItem,
  IFactFindEntity,
  IViewfactFindBeneficiaryResponse,
  IViewFactFindRequest,
} from '@zeta-types';
import { ConflictsDialog } from '../../../components/conflicts-dialog';
import { getClientFeatureAccess } from '../../../helpers/access-control-helper';
import AllTranscripts from '../all-transcripts';
import FactFindHeading from '../common/fact-find-heading';
import CommonTableLayout from '../components/common-table-layout';
import ConfirmNavigationDialog from '../components/confirmation-dialog';
import ErrorDialog from '../components/error-dialog';
import { ErrorSection } from '../components/error-section';
import { IGeneralFieldType, RenderArrayFieldWithChildren } from '../components/render-fields';
import SaveDialog from '../components/save-dialog';
import SuccessDialog from '../components/success-dialog';
import FactFindFooter from '../fact-find-layout/fact-find-footer';
import { useSourceControlContext } from '../fact-find-layout/sourcecontrol-context-component';
import Transcript from '../transcript';
import { nomineesDefaultValues, nomineesValidationSchema } from './form-helpers';
import { createFactFindBeneficiaryPayload, mapBeneficiariesToNominees } from './transform';

export const Nominees = ({
  isClient,
  isManagedClient,
  from,
  refetch,
  clientData,
  conflictData,
}: {
  isClient?: boolean;
  isManagedClient?: boolean;
  from: FactFindType;
  refetch?: () => void;
}) => {
  const userAccess = useUserAccessStore((state) => state.userAccess);
  const location = useLocation();
  const { accessibleEntityID } = useUserDetailsStore((state) => state.user);
  let state = (location.state || {}) as { entityData: IFactFindEntity; clientData: IClientCardItem };
  if (isClient) {
    state = { clientData: { clientID: accessibleEntityID } };
  }
  const { data: sourceControlData, mutateAsync: sourceControlMutateAsync, isPending: sourceControlPending } = useSourceControlContext() || {};

  const isAllTranscripts = useBoolean();
  const isTranscript = useBoolean();
  const updateDirtyFieldsState = useDirtyFieldsStore((state) => state.updateDirtyFieldsState);
  const ffErrors = useFactFindErrorStore((state) => state?.ffErrors);
  const updateFFErrorStore = useFactFindErrorStore((state) => state?.updateFFErrorStore);
  const fieldErrors = ffErrors?.find((error) => error.section === InteractionSubSectionType.beneficiary_details)?.missing_fields;

  const [confirmNavigationModal, setConfirmNavigationModal] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);
  const [conflictsDialog, setConflictsDialog] = useState<boolean>(false);

  const isSuccess = useBoolean(false);
  const isError = useBoolean(false);

  const ffConfig = useFactFindConfigStore((state) => state?.factFindConfigDetails?.individualFactFindSectionConfig?.nominee);
  const sectionsProcessDetails = useFactFindProcessStore((state) => state.sections);
  const currentSection = sectionsProcessDetails?.find((section) => section.section_name === InteractionSubSectionType.beneficiary_details)?.status;
  const planningStatus = location?.state?.clientData?.planningStatus;
  const readOnly = getClientFeatureAccess(userAccess?.access, 'Advisor-Client-FF', planningStatus).isReadOnly;
  const isReadOnly =
    from === FactFindType.PARA_PLANNER ||
    currentSection === IFactFindProcessStatus.pending ||
    currentSection === IFactFindProcessStatus.started ||
    isClient ||
    readOnly;

  type nomineesValidationSchemaType = InferType<typeof nomineesValidationSchema>;

  const defaultEnums = useDefaultEnumsStore((state) => state.defaultEnums);
  const relationshipTypeOptions = defaultEnums?.relationshipType?.map((e) => e.relationshipType) ?? [];

  const methods = useForm<nomineesValidationSchemaType>({ defaultValues: nomineesDefaultValues, resolver: yupResolver(nomineesValidationSchema) });

  const { reset } = methods;

  const fieldArrayNominees = useFieldArray({ name: 'nominees', control: methods.control });

  const isDirtyInPage = methods.formState.isDirty;

  useEffect(() => {
    if (isDirtyInPage) {
      updateDirtyFieldsState(true);
    }
  }, [isDirtyInPage, updateDirtyFieldsState]);

  const blocker = useBlocker(({ nextLocation, currentLocation }) => {
    return isDirtyInPage && currentLocation.pathname !== nextLocation.pathname;
  });

  useEffect(() => {
    if (blocker.state === 'blocked' && showPrompt) {
      setConfirmNavigationModal(true);
    } else {
      blocker?.proceed?.();
    }
  }, [blocker, showPrompt]);

  const handleDiscardAndProceed = () => {
    setConfirmNavigationModal(false);
    blocker?.proceed?.();
    setShowPrompt(false);
    updateDirtyFieldsState(false);
  };

  const handleCloseNavigationModal = () => {
    setConfirmNavigationModal(false);
    blocker?.reset?.();
    setShowPrompt(true);
  };

  const viewNomineesApi = useMutation({
    mutationFn: (payload: IViewFactFindRequest) => InvestorService().viewBeneficiaries(payload),

    onSuccess: (response: AxiosResponse<IViewfactFindBeneficiaryResponse>) => {
      const { data: responseData } = response;

      const nomineesData = mapBeneficiariesToNominees(responseData.beneficiaries);

      methods.reset({ nominees: nomineesData });
    },
  });

  useEffect(() => {
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      viewNomineesApi.mutateAsync({ clientID: state.clientData.clientID, forExternalUse: true });
    }
    if (from === FactFindType.AI) {
      const nomineesData = mapBeneficiariesToNominees(clientData?.beneficiaries);
      reset({ nominees: nomineesData });
    }
  }, [reset, state.clientData.clientID, from, clientData?.beneficiaries]);

  const captureNomineesApi = useMutation({
    mutationFn: (payload: ICaptureFactFindBeneficiaryRequest) => InvestorService().captureBeneficiaries(payload),

    onSuccess: () => {
      methods.reset({}, { keepValues: true });
      viewNomineesApi.mutateAsync({ clientID: state?.clientData?.clientID, forExternalUse: true });
      sourceControlMutateAsync({ clientID: state?.clientData?.clientID });
      blocker?.reset?.();
      setShowPrompt(false);
      enqueueSnackbar('Nominees saved successfully', { variant: 'success' });
    },

    onError: () => {
      isError.onTrue();
      setTimeout(() => {
        isError.onFalse();
      }, 3000);
    },
  });

  const captureNomineesApiAI = useMutation({
    mutationFn: (payload: IPatchAIInteractionSectionRequest) => AIInteractionService().patchClientData(payload),

    onSuccess: () => {
      methods.reset({}, { keepValues: true });
      refetch?.();
      sourceControlMutateAsync({ clientID: state.clientData.clientID });
      blocker?.reset?.();
      setShowPrompt(false);
      enqueueSnackbar('Nominees saved successfully', { variant: 'success' });
      updateFFErrorStore({ ffErrors: null });
    },

    onError: () => {
      isError.onTrue();
      setTimeout(() => {
        isError.onFalse();
      }, 3000);
    },
  });

  const onSubmit = (formData: nomineesValidationSchemaType) => {
    const payload = { clientID: state?.clientData?.clientID, beneficiaries: createFactFindBeneficiaryPayload(formData) };
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      captureNomineesApi.mutateAsync(payload);
    }
    if (from === FactFindType.AI) {
      const aiPayload = {
        client_id: state.clientData.clientID,
        section: InteractionSubSectionType.beneficiary_details,
        data: { beneficiaries: createFactFindBeneficiaryPayload(formData) },
      };
      captureNomineesApiAI.mutate(aiPayload);
    }
  };

  const nomineeFields: IGeneralFieldType[] = [
    { label: 'First Name', name: 'firstName', component: RHFTextField, show: ffConfig?.fields?.firstName?.active ?? true },
    { label: 'Middle Name', name: 'middleName', component: RHFTextField, show: ffConfig?.fields?.middleName?.active ?? true },
    { label: 'Last Name', name: 'lastName', component: RHFTextField, show: ffConfig?.fields?.lastName?.active ?? true },

    {
      label: 'Relationship',
      name: 'relationship',
      component: RHFAutocomplete,
      options: relationshipTypeOptions,
      show: ffConfig?.fields?.relationship?.active ?? true,
    },
    { label: 'Date of Birth', name: 'dateOfBirth', component: RHFDatePicker, show: ffConfig?.fields?.dateOfBirth?.active ?? true },
    {
      label: 'Percentage',
      name: 'percentage',
      component: RHFReactNumberFormat,
      textAlign: 'right',
      decimalScale: 2,
      InputProps: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
      show: ffConfig?.fields?.percentage?.active ?? true,
    },
    { label: 'Address Line 1', name: 'addressLine1', component: RHFTextField, show: ffConfig?.fields?.addressLine1?.active ?? true },
    { label: 'Address Line 2', name: 'addressLine2', component: RHFTextField, show: ffConfig?.fields?.addressLine2?.active ?? true },
    { label: 'City', name: 'city', component: RHFTextField, show: ffConfig?.fields?.city?.active ?? true },
    { label: 'Country', name: 'country', component: RHFTextField, show: ffConfig?.fields?.country?.active ?? true },
    { label: 'Zipcode', name: 'postcode', component: RHFTextField, show: ffConfig?.fields?.postcode?.active ?? true },
  ];

  return (
    <Box>
      {isTranscript.value && <Transcript section="Beneficiaries" isTranscript={isTranscript} />}

      {isAllTranscripts.value && <AllTranscripts isAllTranscripts={isAllTranscripts} />}

      {!isAllTranscripts.value && !isTranscript.value && (
        <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
          {viewNomineesApi.isPending || sourceControlPending ? (
            <FullPageSpinner />
          ) : (
            <>
              <Stack direction="row" alignItems="center">
                <FactFindHeading
                  sectionName="Nominees"
                  isAllTranscripts={isAllTranscripts}
                  isTranscript={isTranscript}
                  hideButton={sourceControlData?.dataSourceMap?.Beneficiaries === 'ManualInput' || isReadOnly}
                  hideAllButton={isClient || [FactFindType.AI, FactFindType.PARA_PLANNER].includes(from) || isManagedClient}
                />

                {conflictData?.length > 0 && (
                  <Tooltip title="Click here to view all the conflicts">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => {
                        setConflictsDialog(true);
                      }}
                      sx={{ marginTop: '-12px' }}
                    >
                      <WarningRounded />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              {fieldErrors?.length > 0 && <ErrorSection errors={fieldErrors} />}

              <Stack spacing={1} mb={8}>
                {isReadOnly && fieldArrayNominees?.fields?.length === 0 && (
                  <Card>
                    <Stack direction="row" justifyContent="center" alignItems="center" height="60vh">
                      <EmptyContent description="No nominee information has been captured as part of the fact-finding process." />
                    </Stack>
                  </Card>
                )}

                {fieldArrayNominees?.fields?.length > 0 && (
                  <CommonTableLayout>
                    <RenderArrayFieldWithChildren
                      fieldsArray={fieldArrayNominees.fields}
                      fieldLabel="nominees"
                      arrayName="Nominee"
                      fieldArrayType={nomineeFields}
                      onRemove={fieldArrayNominees.remove}
                      isClient={isClient}
                      isReadOnly={isReadOnly}
                    />
                  </CommonTableLayout>
                )}
                {isClient || isReadOnly ? null : (
                  <Stack flexDirection="row" marginTop={1}>
                    <Button
                      variant="text"
                      color="primary"
                      id="addNomineeButton"
                      sx={{ marginTop: 1 }}
                      size="small"
                      startIcon={<PlusIcon />}
                      onClick={() => {
                        fieldArrayNominees.append({
                          firstName: '',
                          middleName: '',
                          lastName: '',
                          relationship: '',
                          dateOfBirth: null,
                          percentage: 0,
                          addressLine1: '',
                          addressLine2: '',
                          city: '',
                          country: '',
                          postcode: '',
                        });
                      }}
                    >
                      Add Nominee
                    </Button>
                  </Stack>
                )}
              </Stack>
            </>
          )}

          {isClient ? null : (
            <>
              {[FactFindType.FACT_FIND, FactFindType.AI].includes(from) && (
                <FactFindFooter
                  from={from}
                  isManagedClient={isManagedClient}
                  hideNextStep={isManagedClient || from === FactFindType.AI}
                  isReadOnly={isReadOnly}
                />
              )}
              <SaveDialog open={captureNomineesApi.isPending} />
              <SuccessDialog open={isSuccess.value} />
              <ErrorDialog open={isError.value} />
              <ConfirmNavigationDialog open={confirmNavigationModal} handleClose={handleCloseNavigationModal} data={{ handleDiscardAndProceed, blocker }} />
              <ConflictsDialog
                open={conflictsDialog}
                handleClose={() => setConflictsDialog(false)}
                tableData={conflictData || []}
                isPending={false}
                title={'Nominees'}
              />
            </>
          )}
        </FormProvider>
      )}
    </Box>
  );
};
